import React from "react";
import { useTranslation } from "../../localization/useTranslation";
import { Box, TableCell, TableRow } from "@mui/material";
import {
    DotLegalEmptyState,
    DotLegalOverflowMenu,
    DotLegalPageHeader,
    DotLegalTableWithControls,
    ITableHeader,
    DotLegalButton,
} from "@dotlegal/dotlegal-ui-components";
import { useTags } from "./Tags.hooks";
import { useUserContext } from "../../auth/userContextProvider/UserContextProvider";
import { useMasterDataBreadCrumbProvider } from "../useMasterDataBreadCrumbProvider";
import { TagViewModel } from "./Tags.types";
import TagDialog from "./tagDialog/TagDialog";
import EmptyStateIcon from "../../common/svgs/emptyState.svg?react";
import DeleteTagDialog from "./deleteTagDialog/DeleteTagDialog";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export interface TagsProps {}

function Tags() {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { getBreadCrumbs } = useMasterDataBreadCrumbProvider();
    const {
        isLoading,
        data,
        showTagDialog,
        setShowTagDialog,
        selectedTag,
        setSelectedTag,
        onTagSave,
        showDeleteTagDialog,
        setShowDeleteTagDialog,
        onTagDeleted,
    } = useTags();

    const getMenuItems = (tag: TagViewModel) => {
        if (permissions.canManageMasterData) {
            return [
                {
                    menuItem: translateString("edit"),
                    onClick: () => {
                        setSelectedTag(tag);
                        setShowTagDialog(true);
                    },
                },
                {
                    menuItem: translateString("deleteTag"),
                    onClick: () => {
                        setShowDeleteTagDialog(tag);
                    },
                },
            ];
        }

        return [];
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader
                breadCrumbs={getBreadCrumbs(translateString("tags"))}
                rightComponent={
                    permissions.canManageMasterData ? (
                        <DotLegalButton buttonType="primary" size="medium" onClick={() => setShowTagDialog(true)}>
                            {translateString("createTag")}
                        </DotLegalButton>
                    ) : undefined
                }
            />

            <TablePagePaper>
                {isLoading || (data && data.length > 0) ? (
                    <DotLegalTableWithControls
                        internalScroll
                        headers={getHeaders(translateString)}
                        getUserSpecificPageLength={() => 30}
                        hideRowsPerPage
                        defaultOrderBy={"name"}
                        defaultOrder={"asc"}
                        isLoading={isLoading}
                        clickableRows={false}
                        hasOverflowMenu
                        labelRowsPerPage={translateString("showEntites")}
                        labelSearch={translateString("search")}
                        noOptionsLabel={translateString("noOptions")}
                        paginationLabelOf={translateString("labelOf")}
                        emptyText={translateString("noData")}
                        data={data}
                        renderRow={(row, i) => (
                            <TableRow key={row.id}>
                                <TableCell sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>{row.name}</TableCell>
                                <TableCell>{row.description}</TableCell>
                                <TableCell align={"right"}>
                                    <DotLegalOverflowMenu items={getMenuItems(row)} />
                                </TableCell>
                            </TableRow>
                        )}
                    />
                ) : (
                    <DotLegalEmptyState icon={<EmptyStateIcon />} text={translateString("noTagsEmptyState")} />
                )}
            </TablePagePaper>

            {showDeleteTagDialog && (
                <DeleteTagDialog onCloseDialog={() => setShowDeleteTagDialog(undefined)} onDeleted={() => onTagDeleted()} selectedTag={showDeleteTagDialog} />
            )}
            {showTagDialog && (
                <TagDialog
                    onHideDialog={() => {
                        setSelectedTag(undefined);
                        setShowTagDialog(false);
                    }}
                    onSave={onTagSave}
                    selectedTag={selectedTag}
                />
            )}
        </Box>
    );
}

export default Tags;

function getHeaders(translateString: any) {
    let headers = Array<ITableHeader<TagViewModel>>();
    headers.push({ property: "name", text: translateString("name"), align: "left", showOnMobile: true });
    headers.push({ property: "description", text: translateString("description"), align: "left", showOnMobile: true });

    return headers;
}
